import { UNASSIGNED_FILL } from "../map/color";
import {
  categoryCounts,
  fillForUnit,
  levelOptions,
  matchingUnits,
  representativeUnit,
  selectBadge,
} from "../map/filters";
import type { FilterState } from "../map/filters";
import { CATEGORY_LABEL } from "../map/model";
import type { Category, CategoryStyle, MapData } from "../map/model";
import type { FilterTargets } from "../svg/map";

const ORDER: Category[] = ["restaurant", "shop", "office", "carpark", "venue_rental", "other"];

/**
 * Kategori + kat filtresi — TEK durum, TEK `apply()`.
 *
 * Eskiden iki ayrı modül vardı (ui/filter.ts, ui/level.ts): kategori filtresi
 * yalnızca soluklaştırıyor, kat filtresi ise rengi yazıyordu. Sonuç: "Offices"
 * seçilince parseller giriş katının (Restaurant) rengiyle kırmızı kalıyordu ve
 * iki koşul FARKLI üniteler üzerinde değerlendiği için "Offices + Level 0" gibi
 * kombinasyonlar hatalı eşleşiyordu.
 *
 * Artık her iki koşul da AYNI ünite üzerinde birlikte (AND) aranır; parselin
 * rengi ve rozeti, filtreyi sağlayan ünitelerin EN ALT katındakinden gelir —
 * yani harita her zaman filtrelenen şeyi gösterir.
 */
export interface FilterHandle {
  /**
   * Filtreyi, verilen kiracıyı GÖRÜNÜR kılacak en yakın duruma getirir.
   * Arama sonucu seçildiğinde çağrılır: aksi halde filtrelenmiş bir kiracıyı
   * arayıp seçince harita solmuş/tıklanamaz bir parsele odaklanıyordu.
   */
  reveal(category: Category, level: string): void;
}

export function createFilters(
  data: MapData,
  host: HTMLElement,
  targets: FilterTargets,
  stylesByCategory: Map<Category, CategoryStyle>,
): FilterHandle {
  // Çipler HARİTADA BOYALI olan her kategoriyi kapsamalı. Bir kategoriye ait
  // parsel çizilmiş ama o kategori çip listesinde yoksa, parsel kalıcı olarak
  // soluk kalır (hiçbir çip onu aktif edemez).
  const onMap = new Set<Category>();
  for (const { loc } of targets.parcels) for (const u of loc.units) onMap.add(u.category);
  const totals = categoryCounts(data.locations, null);
  // Panelde rengi tanımlı HER kategori çip olarak görünür — o kategoride henüz
  // hiç mekân olmasa bile. Aksi halde yeni bir kategori (ör. Other) ilk mekân
  // atanana kadar haritada hiç görünmüyor, müşteri de eklendiğini göremiyordu.
  const present = ORDER.filter(
    (c) => stylesByCategory.has(c) || (totals.get(c) ?? 0) > 0 || onMap.has(c),
  );
  const levels = levelOptions(data.locations);
  // NOT: burada erken dönülmez. Çubuklar gereksizse ÇİZİLMEZ (aşağıdaki >= 2
  // koşulları), ama `apply()` her hâlükârda çalışmalı: parsel renklerini ve
  // slot başına görünecek TEK rozeti belirleyen tek yer orası. Erken dönülürse
  // tek kategorili/tek katlı bir veri setinde haritada hiç rozet çıkmıyordu.

  const state: { cats: Set<Category>; level: string | null } = {
    cats: new Set(present),
    level: null,
  };

  // ---- Kategori çubuğu -----------------------------------------------------
  const catChips = new Map<Category, { btn: HTMLButtonElement; count: HTMLSpanElement }>();
  let catAllBtn: HTMLButtonElement | null = null;

  if (present.length >= 2) {
    const bar = document.createElement("div");
    bar.className = "cat-filter";

    catAllBtn = document.createElement("button");
    catAllBtn.type = "button";
    catAllBtn.className = "cat-chip cat-all active";
    catAllBtn.innerHTML = `<span>All</span>`;
    catAllBtn.addEventListener("click", () => {
      present.forEach((c) => state.cats.add(c));
      apply();
    });
    bar.appendChild(catAllBtn);

    for (const cat of present) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "cat-chip active";
      btn.style.setProperty("--c", stylesByCategory.get(cat)?.fill ?? "#94a3b8");
      btn.innerHTML =
        `<span class="cat-dot"></span>` +
        `<span class="cat-name">${CATEGORY_LABEL[cat]}</span>` +
        `<span class="cat-count"></span>`;
      btn.addEventListener("click", () => {
        if (state.cats.has(cat)) state.cats.delete(cat);
        else state.cats.add(cat);
        apply();
      });
      catChips.set(cat, { btn, count: btn.querySelector<HTMLSpanElement>(".cat-count")! });
      bar.appendChild(btn);
    }
    host.appendChild(bar);
  }

  // ---- Kat çubuğu ----------------------------------------------------------
  const levelChips = new Map<string, HTMLButtonElement>();
  let levelAllBtn: HTMLButtonElement | null = null;

  if (levels.length >= 2) {
    const bar = document.createElement("div");
    bar.className = "level-filter";
    bar.setAttribute("role", "tablist");
    bar.setAttribute("aria-label", "Level");

    levelAllBtn = document.createElement("button");
    levelAllBtn.type = "button";
    levelAllBtn.className = "level-chip active";
    levelAllBtn.setAttribute("role", "tab");
    levelAllBtn.textContent = "All";
    levelAllBtn.addEventListener("click", () => { state.level = null; apply(); });
    bar.appendChild(levelAllBtn);

    for (const level of levels) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "level-chip";
      btn.setAttribute("role", "tab");
      btn.textContent = level;
      btn.addEventListener("click", () => { state.level = level; apply(); });
      levelChips.set(level, btn);
      bar.appendChild(btn);
    }
    host.appendChild(bar);
  }

  apply();

  return {
    reveal(category, level) {
      let changed = false;
      if (present.includes(category) && !state.cats.has(category)) {
        state.cats.add(category);
        changed = true;
      }
      // Başka bir kat seçiliyse o kiracının katına geç; "All" ise dokunma.
      if (state.level !== null && state.level !== level && levels.includes(level)) {
        state.level = level;
        changed = true;
      }
      if (changed) apply();
    },
  };

  function apply(): void {
    const s: FilterState = state;

    // Çipler: kategori sayaçları SEÇİLİ KATA göre yeniden hesaplanır; hiç
    // eşleşmeyen çipler soluklaşır (ama tıklanabilir kalır).
    const counts = categoryCounts(data.locations, s.level);
    catChips.forEach(({ btn, count }, cat) => {
      const n = counts.get(cat) ?? 0;
      count.textContent = String(n);
      btn.classList.toggle("active", s.cats.has(cat));
      btn.classList.toggle("empty", n === 0);
      btn.setAttribute("aria-pressed", String(s.cats.has(cat)));
    });
    catAllBtn?.classList.toggle("active", present.every((c) => s.cats.has(c)));

    levelAllBtn?.classList.toggle("active", s.level === null);
    levelChips.forEach((btn, level) => {
      btn.classList.toggle("active", s.level === level);
      const has = data.locations.some((loc) => matchingUnits(loc, { cats: s.cats, level }).length > 0);
      btn.classList.toggle("empty", !has);
      btn.setAttribute("aria-selected", String(s.level === level));
    });

    // Parseller: eşleşen ünitelerin en alt katının rengi; eşleşme yoksa nötr
    // gri + soluk. (Eski kod eşleşmeyen parselin rengine hiç dokunmadığı için
    // bir önceki katın rengi "bayat" kalıyordu — renklerin karışma sebebi.)
    for (const { loc, el } of targets.parcels) {
      // Renk ve "aktif ünite" AYNI temsilciden türer — ayrışamazlar.
      const rep = representativeUnit(loc, s);
      const fill = rep ? fillForUnit(loc, rep, stylesByCategory, UNASSIGNED_FILL) : null;
      el.classList.toggle("filtered-out", fill === null);
      el.setAttribute("fill", fill ?? UNASSIGNED_FILL);
      // Parselin GÖVDESİNE tıklanınca sidebar'ın hangi kiracıyı açacağı: parselin
      // o an gösterdiği kiracı. Aksi halde "Offices" filtresinde yeşil bir parsele
      // tıklayınca giriş katının restoranı açılıyordu.
      el.dataset.activeLevel = rep?.floorLevel ?? "";
      el.dataset.activeCategory = rep?.category ?? "";
    }

    // İç dikişler parsellerle AYNI renge boyanır — aksi halde filtre kat
    // değiştirip parselleri yeniden renklendirdiğinde dikiş bayat renkte kalıp
    // birleşik parselin ortasında çizgi gibi görünürdü.
    for (const { loc, el } of targets.seams) {
      const rep = representativeUnit(loc, s);
      const fill = rep ? fillForUnit(loc, rep, stylesByCategory, UNASSIGNED_FILL) : null;
      el.classList.toggle("filtered-out", fill === null);
      el.setAttribute("stroke", fill ?? UNASSIGNED_FILL);
    }

    // Rozetler: slot başına TEK rozet — kategori VE kat koşulunu sağlayanların
    // en alt kattakisi. Hiçbiri sağlamıyorsa o slotta rozet görünmez.
    for (const { badges } of targets.badgeSlots) {
      const chosen = selectBadge(badges.map((b) => b.info), s);
      for (const b of badges) b.el.classList.toggle("badge-hidden", b.info !== chosen);
    }
  }
}
